import { useCallback } from 'react';
import { editComplete as serviceEditComplete } from '@decisions/server/DecisionsFramework/Design/Properties/ContextProviderService';
import { EditCompleteType } from '@decisions/server/DecisionsFramework/Design/Properties/EditCompleteType';
import { useBeforeSavePromises } from 'hooks/useBeforeSavePromises';

export interface IUseEditComplete {
  editingContextId: string;
}

export interface IAddBeforeSavePromises {
  /** A promise that needs to be `await`ed proceeding */
  addBeforeSavePromise?: (id: string, promise: Promise<any>) => void;
  /** Remove a promise that needs to be `await`ed proceeding. */
  removeBeforeSavePromise?: (id: string) => void;
}

/**
 * Hook to track editing promises to make sure they complete prior to EditComplete being called.
 *
 * Copyright (c) 2021 Decisions All Rights Reserved
 */
export function useEditComplete({ editingContextId }: IUseEditComplete) {
  const doCancel = useCallback(async () => {
    try {
      await serviceEditComplete({
        completeType: EditCompleteType.Canceled,
        contextProviderRegistrationId: editingContextId,
      });
      return true;
    } catch (error) {
      console.error('Error on EditComplete Cancel', error);
      return false;
    }
  }, [editingContextId]);

  const editCompleteSave = useCallback(async () => {
    // _then_ Edit Complete:
    try {
      await serviceEditComplete({
        completeType: EditCompleteType.Saved,
        contextProviderRegistrationId: editingContextId,
      });
      return true;
    } catch (error) {
      console.error('Error on EditComplete Save', error);
      return false;
    }
  }, [editingContextId]);

  const { addBeforeSavePromise, removeBeforeSavePromise, save, cancel } = useBeforeSavePromises({
    save: editCompleteSave,
    cancel: doCancel,
  });

  const editComplete = useCallback(
    async (completeType: EditCompleteType) => {
      if (!editingContextId) {
        console.warn('Attempted edit complete but ID was falsy');
        return; // bail out if the editingContext ID isn't ready or is already gone
      }

      if (completeType === EditCompleteType.Saved) {
        // wait for any in-flight handlers (setValue, etc.)
        // (not using QueueUtil, because _order_ does not matter here, just that they all finish)
        return await save();
      } else {
        // just cancel
        return await cancel();
      }
    },
    [cancel, editingContextId, save]
  );

  return {
    addBeforeSavePromise,
    editComplete,
    removeBeforeSavePromise,
  };
}
